import { useEffect, useState, useRef, type ReactNode } from "react";
import { useRoute, useLocation, Link } from "wouter";
import {
  Pencil, FileCheck2, ArrowRight, CheckCircle2, ChevronRight, Settings,
  Clock, XCircle, AlertCircle, MapPin,
} from "lucide-react";
import { CompanyDashboardDesktop } from "../components/CompanyDashboardDesktop";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CompanyMediaPreview } from "../components/CompanyMediaPreview";
import { useI18n } from "../lib/i18n";
import { profileKey } from "../lib/profileSession";
import { PullToRefresh } from "../components/PullToRefresh";
import { refreshFamily, useRefreshOwner } from "../lib/useRefreshOwner";

/**
 * v3.9.0 Phase 4 — B2B organization dashboard.
 * The owner hub for one organization: required setup readiness (single-hue
 * magnitude meter + hero %), verification/licenses status (reserved status
 * colors, each with an icon + label), business details and next-step CTAs.
 */
export default function CompanyDashboard() {
  const { t } = useI18n();
  const [, params] = useRoute("/company/:id/dashboard");
  const id = params?.id || "";
  const [, navigate] = useLocation();
  const { user, setUser, loading: authLoading } = useAuth();
  const [desktop, setDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 640px)");
    const update = () => setDesktop(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const identity = `${profileKey(user)}:company:${id}`;
  const [overview, setOverview] = useState<{ identity: string; data: Awaited<ReturnType<typeof api.companyDashboard>> } | null>(null);
  // A route change can reuse this page. Retention belongs only to the same
  // Business/profile, even if the next owner's initial read fails.
  const d = overview?.identity === identity ? overview.data : null;
  const [profileError, setProfileError] = useState<string | null>(null);
  const [mutation, setMutation] = useState(false);
  const refresh = useRefreshOwner(identity, signal => [refreshFamily(async () => {
    const response = await api.companyDashboard(id, signal);
    if (!response?.company) throw new Error("Missing Business overview.");
    if (!response.company.historyOnly && (!response.completeness || !response.licenses)) throw new Error("Missing Business overview.");
    return response;
  }, response => setOverview({ identity, data: response }), () => setOverview(null))], { enabled: !!user });
  const mutationChanged = (active: boolean) => { if (active) refresh.cancel(); setMutation(active); };
  const withRefresh = (content: ReactNode) => <PullToRefresh identity={`${identity}:${mutation}`} disabled={mutation || (refresh.reading && !d)} onRefresh={refresh.onRefresh}>
    {refresh.error && <p role="alert" className="container-x py-3 text-sm text-brand-red">{refresh.error}</p>}{content}
  </PullToRefresh>;

  useEffect(() => { if (!authLoading && !user) navigate("/login"); }, [authLoading, user, navigate]);

  // [#10] Leaving company scope returns to the personal profile explicitly, so the
  // personal dashboard (which redirects while a company is active) doesn't bounce.
  async function goPersonal() {
    refresh.cancel();
    setProfileError(null);
    try {
      const selected = await api.switchContext(null);
      if (!selected) throw new Error("Could not switch profiles. Please retry.");
      setUser(selected); navigate("/dashboard");
    } catch (failure: unknown) {
      setProfileError(failure instanceof Error ? failure.message : "Could not switch profiles. Please retry.");
    }
  }

  if (authLoading || !refresh.loaded) return withRefresh(<div className="container-x py-16 text-center text-mut">{t("company.dashboard.loading")}</div>);
  if (!d) return withRefresh(<div className="container-x py-16 text-center"><button onClick={goPersonal} className="btn-outline">{t("company.dashboard.back")}</button>{profileError && <p role="alert">{profileError}</p>}</div>);

  const { company: c, completeness, licenses, myRole } = d;
  const canManage = myRole === "owner";

  if (c.deletedAt || c.historyOnly) return (
    <div className="container-x py-6 max-w-3xl">
      <button onClick={goPersonal} className="text-sm text-mut hover:text-navy">← Personal dashboard</button>
      <h1 className="mt-4 text-2xl font-extrabold text-navy">{c.name}</h1>
      <div className="card p-5 mt-4">
        <h2 className="font-bold text-navy">{c.deletedAt ? "Business profile deleted" : "Business history"}</h2>
        <p className="mt-2 text-sm text-mut">{c.deletedAt ? "Its public page and postings are closed." : "This business is now managed by its owner."} Your existing transaction records remain here. You can continue your existing dealings under their original permissions. New activity and private business management require the owner.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link href="/contracts" className="btn-outline">Transactions</Link>
          <Link href="/wallet" className="btn-outline">Wallet</Link>
          <Link href="/messages" className="btn-outline">Messages</Link>
          {canManage && <Link href={`/company/${c.id}/manage`} className="btn-outline">Business settings</Link>}
        </div>
      </div>
      {profileError && <p role="alert" className="mt-2 text-sm text-brand-red">{profileError}</p>}
    </div>
  );

  if (desktop) return withRefresh(<CompanyDashboardDesktop key={id} dashboard={d} accountName={[user?.firstName, user?.lastName].filter(Boolean).join(" ")}
    onMutationChange={mutationChanged}
    onPersonal={() => void goPersonal()} profileError={profileError}
    onChanged={() => refresh.reload()}
    onBusinessNavigate={async (href) => {
      setProfileError(null);
      try {
        if (user?.activeContext !== `company:${id}`) {
          const selected = await api.switchContext(`company:${id}`);
          if (!selected) throw new Error("Could not switch profiles. Please retry.");
          setUser(selected);
        }
        navigate(href);
      } catch (failure: unknown) { setProfileError(failure instanceof Error ? failure.message : "Could not switch profiles. Please retry."); }
    }} />);

  return withRefresh(
    <div className="min-w-0 pb-24">
        <button onClick={goPersonal} className="flex min-h-14 items-center px-5 text-xs text-mut hover:text-navy">← {t("company.a.personal", "Personal dashboard")}</button>
        {profileError && <p role="alert" className="px-5 pb-3 text-sm text-brand-red">{profileError}</p>}

      {/* Header */}
      <section className="border-y border-line bg-white">
        <div className="h-28 overflow-hidden bg-gradient-to-br from-navy-50 via-slate-200 to-slate-300">
          {c.coverObject && <CompanyMediaPreview companyId={c.id} kind="cover" revision={c.coverObject} alt="" className="h-full w-full object-cover" />}
        </div>
        <div className="px-5 pb-5">
          <LogoBlock key={id} company={c} canManage={canManage} t={t} onMutationChange={mutationChanged} onChange={() => refresh.reload()} />
          <h1 className="mt-3 break-words text-2xl font-extrabold leading-tight tracking-tight text-navy">{c.name}</h1>
          <div className="mt-2 flex items-start gap-1 text-xs text-mut">
            {c.city && <><MapPin className="h-3.5 w-3.5 shrink-0 text-brand-red" /><span className="min-w-0 break-words">{c.city}</span><span aria-hidden="true">·</span></>}
            <span>{canManage ? t("company.a.owner", "Owner") : t("company.a.member", "Member")}</span>
          </div>
          {c.verified && <Link href={`/c/${c.id}`} className="btn-outline mt-4 min-h-11 w-full justify-center">{t("company.a.viewPublic", "View public profile")}</Link>}
        </div>
      </section>

      {/* Quick actions */}
      {canManage && (
        <section className="px-5 py-6">
          <h2 className="mb-3 text-base font-bold text-navy">{t("company.a.manageBusiness", "Manage your business")}</h2>
          <div className="grid grid-cols-3 gap-2">
            <Link href={`/company/${c.id}/edit`} className="flex min-h-24 min-w-0 flex-col items-center justify-center gap-3 rounded-xl border border-line bg-white px-1 py-3 text-center text-xs font-semibold text-navy"><Pencil className="h-5 w-5" />{t("company.dashboard.editProfile")}</Link>
            <Link href={`/company/${c.id}/licenses`} className="flex min-h-24 min-w-0 flex-col items-center justify-center gap-3 rounded-xl border border-line bg-white px-1 py-3 text-center text-xs font-semibold text-navy"><FileCheck2 className="h-5 w-5" />{t("company.dashboard.verification")}</Link>
            <Link href={`/company/${c.id}/manage`} className="flex min-h-24 min-w-0 flex-col items-center justify-center gap-3 rounded-xl border border-line bg-white px-1 py-3 text-center text-xs font-semibold text-navy"><Settings className="h-5 w-5" />{t("company.a.settings", "Settings")}</Link>
          </div>
        </section>
      )}

      <div className={`grid gap-4 ${canManage ? "" : "mt-5"}`}>
        {/* Setup readiness is independent of optional enrichment and verification. */}
        <section className="border-y border-line bg-white p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-bold text-navy">{t("company.dashboard.completeness")}</h2>
            <span className="text-base font-extrabold text-navy">{completeness.percent}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-navy/10 overflow-hidden" role="progressbar" aria-label={t("company.dashboard.completeness")} aria-valuenow={completeness.percent} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full bg-navy rounded-full transition-all" style={{ width: `${completeness.percent}%` }} />
          </div>
          <p className="mt-3 text-xs text-mut">{completeness.done}/{completeness.total} {t("company.dashboard.sections")}</p>
          {completeness.missing.length > 0 ? (
            <div className="mt-3">
              <div className="text-xs font-semibold text-mut uppercase tracking-wide mb-1.5">{t("company.dashboard.stillMissing")}</div>
              <ul className="text-sm text-navy space-y-1">
                {completeness.missing.slice(0, 6).map((m: string) => (
                  <li key={m} className="flex items-center gap-2"><AlertCircle className="w-3.5 h-3.5 text-brand-gold shrink-0" />{t(`company.completeness.${m}`, m)}</li>
                ))}
              </ul>
              {canManage && <Link href={`/company/${c.id}/edit`} className="btn-outline h-8 px-3 text-xs mt-3">{t("company.dashboard.completeProfile")}<ArrowRight className="w-3.5 h-3.5" /></Link>}
            </div>
          ) : <p className="text-sm text-brand-greendark mt-3 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />{t("company.dashboard.profileComplete")}</p>}
        </section>

        {/* Verification / licenses (status colors + icon + label) */}
        <section className="border-y border-line bg-white p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-base font-bold text-navy">{t("company.a.registration", "Registration")}</h2>
            {canManage && <Link href={`/company/${c.id}/licenses`} className="inline-flex min-h-11 items-center text-xs font-semibold text-brand-red">{t("company.a.manage", "Manage")}</Link>}
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <StatusTile Icon={CheckCircle2} n={licenses.approved} label={t("company.a.approved", "Approved")} tone="green" />
            <StatusTile Icon={Clock} n={licenses.pending} label={t("company.dashboard.inReview")} tone="gold" />
            <StatusTile Icon={XCircle} n={licenses.rejected} label={t("company.dashboard.rejected")} tone="red" />
          </div>
          {!c.verified && <div className="mt-4 border-t border-line pt-4 text-xs text-mut"><strong className="text-navy">Private business profile</strong><p className="mt-1">{licenses.pending > 0 ? "Your registration is under Admin review. Posting, transactions and interactions with postings unlock after verification is approved." : "Submit registration proof to unlock public display, posting, transactions and interactions with postings."}</p>{canManage && <Link href={`/company/${c.id}/licenses`} className="mt-2 inline-flex min-h-11 items-center font-semibold text-brand-red">{t("company.dashboard.licenses")}</Link>}</div>}
        </section>

        {/* Organization details */}
        <details className="group mx-5 border-b border-line pb-4">
          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-navy [&::-webkit-details-marker]:hidden">{t("company.a.businessDetails", "Business details")}<ChevronRight className="h-4 w-4 text-mut group-open:rotate-90" /></summary>
          <dl className="mt-3 space-y-3 text-xs">
            <Row k={t("company.dashboard.type")} v={c.subtype || (c.organizationType === "SOLE_PROPRIETORSHIP" ? "Sole Proprietorship" : "—")} />
            {c.primaryIndustry && <Row k={t("company.a.industry", "Primary industry")} v={c.primaryIndustry} />}
            {c.city && <Row k={t("company.a.location", "Location")} v={c.city} />}
            <Row k={t("company.dashboard.status")} v={c.operatingStatus || "—"} />
            <Row k={t("company.dashboard.onPlatform")} v={c.since ? `${t("company.dashboard.since")} ${c.since}` : "—"} />
          </dl>
        </details>
      </div>

      <p className="mt-4 bg-navy-50 px-5 py-4 text-xs leading-6 text-mut">{t("company.dashboard.optionalEnrichment", "Branding, classifications and offerings are optional. Verification is reviewed separately.")}</p>
    </div>
  );
}

function StatusTile({ Icon, n, label, tone }: { Icon: typeof CheckCircle2; n: number; label: string; tone: "green" | "gold" | "red" }) {
  const toneCls = tone === "green" ? "text-brand-greendark" : tone === "gold" ? "text-navy" : "text-brand-red";
  return (
    <div className="min-w-0 py-2">
      <div className={`text-2xl font-extrabold leading-none ${toneCls}`}>{n}</div>
      <div className="mt-2 flex items-center justify-center gap-1 text-[10px] text-mut"><Icon className={`h-3 w-3 shrink-0 ${toneCls}`} />{label}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between gap-3"><dt className="text-mut">{k}</dt><dd className="text-navy font-medium text-right break-words">{v}</dd></div>;
}

// v3.9.4 — logo display + upload (owner).
function LogoBlock({ company, canManage, t, onChange, onMutationChange }: { company: { id: string; name: string; initials: string; logoObject?: string | null }; canManage: boolean; t: (key: string, fallback?: string) => string; onChange: () => Promise<void>; onMutationChange: (active: boolean) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [bust, setBust] = useState(0);
  async function upload(f: File) {
    if (f.size > 5 * 1024 * 1024 || !["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      setErr(t("company.a.imageFormat", "Choose a JPEG, PNG or WebP image up to 5 MB.")); return;
    }
    onMutationChange(true); setBusy(true); setErr(null);
    try { await api.uploadCompanyLogo(company.id, f); setBust(Date.now()); await onChange(); }
    catch (failure: unknown) { setErr(failure instanceof Error ? failure.message : t("company.dashboard.uploadFailed")); }
    finally { setBusy(false); onMutationChange(false); }
  }
  return (
    <div>
      <div className="flex items-end justify-between gap-3">
        <div className="relative -mt-9 h-[72px] w-[72px] shrink-0 overflow-hidden rounded-full border-4 border-white bg-navy">
          {company.logoObject
            ? <CompanyMediaPreview companyId={company.id} kind="logo" revision={`${company.logoObject}:${bust}`} alt={company.name} className="h-full w-full object-cover" fallback={<div className="grid h-full w-full place-items-center text-xl font-extrabold text-white">{company.initials}</div>} />
            : <div className="grid h-full w-full place-items-center text-xl font-extrabold text-white">{company.initials}</div>}
        </div>
      {canManage && (
        <>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.currentTarget.value = ""; }} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="min-h-11 text-xs font-semibold text-brand-red hover:underline disabled:opacity-60">{busy ? t("common.loading", "Loading…") : company.logoObject ? t("company.dashboard.changeLogo") : t("company.dashboard.addLogo")}</button>
        </>
      )}
      </div>
      {err && <p role="alert" className="mt-2 text-xs text-brand-red">{err}</p>}
    </div>
  );
}
